import { BaseSkeleton } from '../../../types';

export type ContactTier = string;

export interface TierDefinition {
  id: string;
  label: string;
  description: string;
  cadenceDays: number | null;
  sortOrder: number;
}

export type ContactSignalType =
  | 'job_change'
  | 'email_activity'
  | 'calendar_meeting'
  | 'linkedin_post'
  | 'reddit_dm'
  | 'slack_dm'
  | 'twitter_dm'
  | 'twitter_mention'
  | 'github_activity'
  | 'custom';

export type ContactChannel =
  | 'email'
  | 'calendar'
  | 'slack'
  | 'whatsapp'
  | 'phone'
  | 'linkedin'
  | 'reddit'
  | 'twitter'
  | 'github'
  | 'other';

export type ContactIdentityType = 'person' | 'account' | 'ai' | 'unknown';
export type InteractionDirection = 'inbound' | 'outbound' | 'both';

export interface ContactInteraction {
  _id: string;
  contactId: string;
  at: string;               // ISO date
  channel: ContactChannel;
  direction: InteractionDirection;
  snippet?: string;
  source: string;           // "gmail" | "slack" | "reddit" | "manual" | ...
  externalId?: string;      // dedup key from source system
  created_at?: string;
}

export interface ContactSignal {
  id: string;
  type: ContactSignalType;
  title: string;
  body?: string;
  detectedAt: string;
  source: string;
}

export interface ContactLastInteraction {
  at: string;
  channel: ContactChannel;
  snippet?: string;
}

export interface ContactHandles {
  reddit?: string;          // "u/username"
  twitter?: string;         // "@handle" or "handle"
  slack?: string;           // Slack user ID e.g. "U012AB3CD"
  slackWorkspace?: string;  // workspace name / domain
  github?: string;          // "username"
}

export interface ContactSkeleton extends BaseSkeleton {
  '@type': 'Contact';

  // Basic info
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  avatarUrl?: string;

  // Socials (legacy flat fields — kept for backward compat; prefer handles.*)
  linkedinUrl?: string;
  twitterHandle?: string;
  githubHandle?: string;

  // Multi-channel handles
  handles?: ContactHandles;

  // Relationship management
  tier: ContactTier;
  tags?: string[];
  notes?: string;

  // Identity classification
  identityType?: ContactIdentityType;
  relatedContactIds?: string[];

  // Primary channel context (shows in header)
  primaryChannel?: ContactChannel;
  primaryChannelContext?: string;   // e.g. "Acme Inc Slack" | "r/entrepreneur"

  // Interaction tracking
  lastInteraction?: ContactLastInteraction;
  preferredChannel?: ContactChannel;

  // Warmth score (computed, cached)
  warmthScore?: number;             // 0–1
  warmthUpdatedAt?: string;

  // AI-generated intelligence
  topics?: string[];
  signals?: ContactSignal[];
  nextReachOutAt?: string;
  nextReachOutReason?: string;

  // Integration cross-refs
  sources?: string[];
  lastEnrichedAt?: string;
}

export interface ContactEntity {
  _id: string;
  id?: string;
  entityType: 'Contact';
  name: string;
  spaceId?: string;
  created_at: string;
  updated_at?: string;
  skeleton: ContactSkeleton;
}

export const DEFAULT_TIER_CONFIG: TierDefinition[] = [
  { id: 'inner_circle', label: 'Inner Circle', description: 'Drop everything — respond instantly',  cadenceDays: 7,    sortOrder: 0 },
  { id: 'tier1',        label: 'Tier 1',       description: 'Respond soon (within hours)',          cadenceDays: 14,   sortOrder: 1 },
  { id: 'tier2',        label: 'Tier 2',       description: 'Respond within a day or two',          cadenceDays: 30,   sortOrder: 2 },
  { id: 'tier3',        label: 'Tier 3',       description: 'Respond within the week',              cadenceDays: 60,   sortOrder: 3 },
  { id: 'dormant',      label: 'Dormant',      description: 'Low-priority or inactive',             cadenceDays: null, sortOrder: 4 },
];

function _buildMap<V>(tiers: TierDefinition[], accessor: (t: TierDefinition) => V): Record<string, V> {
  return Object.fromEntries(tiers.map((t) => [t.id, accessor(t)]));
}

export const TIER_LABELS: Record<string, string> = _buildMap(DEFAULT_TIER_CONFIG, (t) => t.label);
export const TIER_DESCRIPTIONS: Record<string, string> = _buildMap(DEFAULT_TIER_CONFIG, (t) => t.description);
export const TIER_CADENCE_DAYS: Record<string, number | null> = _buildMap(DEFAULT_TIER_CONFIG, (t) => t.cadenceDays);

export function buildTierLabels(tiers: TierDefinition[]): Record<string, string> { return _buildMap(tiers, (t) => t.label); }
export function buildTierDescriptions(tiers: TierDefinition[]): Record<string, string> { return _buildMap(tiers, (t) => t.description); }
export function buildTierCadenceDays(tiers: TierDefinition[]): Record<string, number | null> { return _buildMap(tiers, (t) => t.cadenceDays); }

export function getWarmthLevel(score: number): 'hot' | 'warm' | 'cool' | 'cold' {
  if (score > 0.7) return 'hot';
  if (score > 0.4) return 'warm';
  if (score > 0.1) return 'cool';
  return 'cold';
}

/** Normalize any legacy or alternate tier values to the canonical 5-tier system */
export function normalizeTier(tier: string): ContactTier {
  switch (tier) {
    case 'inner_circle': return 'inner_circle';
    case 'tier1':        return 'tier1';
    case 'tier2':        return 'tier2';
    case 'tier3':        return 'tier3';
    case 'dormant':      return 'dormant';
    // Map new simplified tiers back to canonical
    case 'close':        return 'inner_circle';
    case 'regular':      return 'tier2';
    case 'archived':     return 'dormant';
    default:             return 'tier3';
  }
}
